/**
 * Raids (v1 ticket 47), the arithmetic the screens need, DOM-free: the army being picked in the armoury (its
 * weight against the budget, its power, which chips still fit), why a friend can't be raided, which card comes
 * next, and what home's one line says. Nothing here decides a result: those come from the server's replies.
 */
import type { ArmouryForm, ArmouryWord, Form, RaidOpen, RaidOverview, Side, Band, Verdict, Struck } from './raid-api';

export const FORM_ICON: Record<Form, string> = { choice: '🏹', listen: '🐉', gap: '🪨', recall: '🔮', dictation: '🌪' };

/** One picked weapon: a word in one of its forms. A word is picked at most once. */
export interface ArmyPick { key: string; form: Form; challenge?: string }

const formOf = (words: readonly ArmouryWord[], p: ArmyPick): ArmouryForm | undefined =>
  words.find((w) => w.key === p.key)?.forms.find((f) => f.form === p.form && f.challenge === p.challenge);

export function armyWeight(words: readonly ArmouryWord[], picks: readonly ArmyPick[]): number {
  return picks.reduce((sum, p) => sum + (formOf(words, p)?.weight ?? 0), 0);
}

/** The picked army's power as the armoury prices it, to one decimal (the server's own rounding). */
export function armyPower(words: readonly ArmouryWord[], picks: readonly ArmyPick[]): number {
  return Math.round(picks.reduce((sum, p) => sum + (formOf(words, p)?.power ?? 0), 0) * 10) / 10;
}

/** A tap on a chip: the same chip again puts the weapon down, another form of the word swaps it, a new word joins the end. */
export function togglePick(picks: readonly ArmyPick[], key: string, form: Form, challenge?: string): ArmyPick[] {
  const had = picks.find((p) => p.key === key);
  if (!had) return [...picks, { key, form, ...(challenge ? { challenge } : {}) }];
  if (had.form === form && had.challenge === challenge) return picks.filter((p) => p.key !== key);
  return picks.map((p) => (p.key === key ? { key, form, ...(challenge ? { challenge } : {}) } : p));
}

/**
 * Whether this chip can be tapped: it is picked already (to put it down), or taking it (in place of the word's other
 * form) fits the budget and, aimed at a district, its words to hit (`maxWeapons`, v1 ticket 48).
 */
export function chipFits(words: readonly ArmouryWord[], picks: readonly ArmyPick[], budget: number, key: string, form: Form, maxWeapons = Infinity, challenge?: string): boolean {
  if (picks.some((p) => p.key === key && p.form === form && p.challenge === challenge)) return true;
  const next = togglePick(picks, key, form, challenge);
  return !!formOf(words, { key, form, ...(challenge ? { challenge } : {}) }) && armyWeight(words, next) <= budget && next.length <= maxWeapons;
}

/** Picks the armoury no longer holds (a word removed meanwhile) are dropped. */
export function validPicks(words: readonly ArmouryWord[], picks: readonly ArmyPick[]): ArmyPick[] {
  return picks.filter((p) => !!formOf(words, p));
}

/**
 * What is left to fill: `full` when the weight, or the district's weapons, are used up (nothing more fits);
 * `none` when room is left but every word that can be used is already picked; else `open`.
 */
export function armyRoom(words: readonly ArmouryWord[], picks: readonly ArmyPick[], budget: number, limit: number): 'open' | 'full' | 'none' {
  const rest = words.filter((w) => !w.resting && !picks.some((p) => p.key === w.key));
  if (rest.some((w) => w.forms.some((f) => chipFits(words, picks, budget, w.key, f.form, limit, f.challenge)))) return 'open';
  return armyWeight(words, picks) < budget && picks.length < limit && !rest.length ? 'none' : 'full';
}

export type RaidBlock = 'pending' | 'full' | 'no-town' | 'limit';

/** Why this friend can't be raided now, or null. The day's launches run out for every friend at once. */
export function raidBlock(raid: RaidOpen, launchesLeft: number): RaidBlock | null {
  if (raid !== 'open') return raid;
  return launchesLeft > 0 ? null : 'limit';
}

export type ReadyReason = RaidBlock | 'no-words' | 'resting';
export type ReadyAction = 'capture' | 'army' | 'friends' | 'wait';
export interface Readiness {
  /** The learner's own side (words, resting, today's launches, an army already on its way). */
  you: { reason: ReadyReason; action: ReadyAction } | null;
  /** The friend's side, only as much as the server already tells a friend (`RaidOpen`): never a count or a word. */
  them: { reason: ReadyReason; action: ReadyAction } | null;
  /** The reason a research event and the first line report: the learner's own, else the friend's. */
  first: ReadyReason | null;
}

/**
 * Real raid entry (#64): whose readiness is missing, and the one valid next step. `words` is undefined until the
 * armoury is read. A friend's readiness is only what `RaidOpen` already carries.
 */
export function raidReadiness(raid: RaidOpen, launchesLeft: number, words: readonly ArmouryWord[] | undefined): Readiness {
  const you: Readiness['you'] = raid === 'pending' ? { reason: 'pending', action: 'army' }
    : words && words.length === 0 ? { reason: 'no-words', action: 'capture' }
      : words && words.every((w) => w.resting) ? { reason: 'resting', action: 'capture' }
        : launchesLeft <= 0 ? { reason: 'limit', action: 'wait' } : null;
  const them: Readiness['them'] = raid === 'full' || raid === 'no-town' ? { reason: raid, action: 'friends' } : null;
  return { you, them, first: you?.reason ?? them?.reason ?? null };
}

/** The first card still to answer at or after `from`, else before it; -1 when none is left. */
export function nextCard(open: readonly boolean[], from = 0): number {
  for (let i = from; i < open.length; i++) if (open[i]) return i;
  for (let i = 0; i < Math.min(from, open.length); i++) if (open[i]) return i;
  return -1;
}

/** A graded answer in a defence, named as the report names it. */
export function struckOf(verdict: Verdict): Struck {
  return verdict === 'right' ? 'blocked' : verdict === 'scratched' ? 'scratched' : 'hit';
}

export type HomeLine =
  | { kind: 'army'; id: string; name: string; weapons: number }
  | { kind: 'report'; id: string; name: string; side: Side; band: Band }
  | { kind: 'spoils'; n: number };

/** Home's one line: the army that has waited longest, else the newest unread report, else words ready to rebuild. */
export function homeLine(o: Pick<RaidOverview, 'incoming' | 'reports'> & { spoils?: { ready: number } } | undefined): HomeLine | null {
  if (!o) return null;
  const army = [...o.incoming].sort((a, b) => a.launchedAt.localeCompare(b.launchedAt))[0];
  if (army) return { kind: 'army', id: army.id, name: army.from.name, weapons: army.weapons };
  const r = o.reports.find((x) => x.unread);
  if (r) return { kind: 'report', id: r.id, name: r.other.name, side: r.side, band: r.band };
  return o.spoils && o.spoils.ready > 0 ? { kind: 'spoils', n: o.spoils.ready } : null;
}

/** Home's mark on the Friends entry: an army waits or a report is unread. */
export function raidWaits(o: Pick<RaidOverview, 'incoming' | 'reports'> & { spoils?: { ready: number } } | undefined): boolean {
  return !!homeLine(o);
}

